import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { generateItinerary } from "@/lib/groq";
import { sendItineraryEmail } from "@/lib/email";
import { useToast } from "@/hooks/use-toast";

interface ItineraryFormProps {
  onItineraryGenerated: (itinerary: string, destination: string) => void;
  setLoading: (loading: boolean) => void;
  itinerary: string;
}

const ItineraryForm = ({ onItineraryGenerated, setLoading, itinerary }: ItineraryFormProps) => {
  const { toast } = useToast();
  const [destination, setDestination] = useState("");
  const [days, setDays] = useState("3");
  const [budget, setBudget] = useState("");
  const [email, setEmail] = useState("");
  const [showEmailField, setShowEmailField] = useState(false);

  // Interest options
  const interestOptions = [
    { id: "museums", label: "Museums & Culture" },
    { id: "food", label: "Food & Cuisine" },
    { id: "outdoors", label: "Outdoor Activities" },
    { id: "history", label: "Historical Sites" },
    { id: "shopping", label: "Shopping" },
    { id: "nightlife", label: "Nightlife" },
    { id: "relaxation", label: "Relaxation" },
    { id: "family", label: "Family Activities" },
  ];
  
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);

  const handleInterestChange = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedInterests([...selectedInterests, id]);
    } else {
      setSelectedInterests(selectedInterests.filter(item => item !== id));
    }
  };

  const handleGenerateItinerary = async () => {
    if (!destination) {
      toast({
        title: "Missing information",
        description: "Please enter a destination.",
        variant: "destructive",
      });
      return;
    }

    if (selectedInterests.length === 0) {
      toast({
        title: "Missing information",
        description: "Please select at least one interest.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      // Convert interests to proper format
      const interestLabels = selectedInterests.map(
        id => interestOptions.find(option => option.id === id)?.label || id
      );
      
      const itinerary = await generateItinerary(
        destination,
        parseInt(days),
        interestLabels,
        budget || undefined
      );
      
      onItineraryGenerated(itinerary, destination);
      setShowEmailField(true);
    } catch (error) {
      console.error("Error generating itinerary:", error);
      toast({
        title: "Error",
        description: "There was an error generating your itinerary. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!email) {
      toast({
        title: "Missing information",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    if (!itinerary) {
      toast({
        title: "Missing itinerary",
        description: "Please generate an itinerary first.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const emailContent = `
Dear Traveler,

Here is your ${days}-day itinerary for ${destination}:

${itinerary}

Additional Information:
- Budget Category: ${budget || 'Not specified'}
- Interests: ${selectedInterests.map(id => interestOptions.find(opt => opt.id === id)?.label).join(', ')}
- Duration: ${days} days

Happy travels!
TravelAI Team
      `;
      
      const success = await sendItineraryEmail(
        email,
        `Your ${days}-day ${destination} Itinerary`,
        emailContent
      );
      
      if (success) {
        toast({
          title: "Success",
          description: `Your itinerary has been sent to ${email}`,
        });
        // Clear the email field after successful send
        setEmail("");
      } else {
        throw new Error("Failed to send email");
      }
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast({
        title: "Error",
        description: error.message === 'Invalid email format' 
          ? "Please enter a valid email address."
          : "There was an error sending your email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="search-card space-y-6">
      <div>
        <Label htmlFor="destination">Destination</Label>
        <Input
          id="destination"
          placeholder="City or Country"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="days">Number of Days</Label>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger id="days">
              <SelectValue placeholder="Select days" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 10, 14].map((day) => (
                <SelectItem key={day} value={day.toString()}>
                  {day} {day === 1 ? "day" : "days"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="budget">Budget (Optional)</Label>
          <Input
            id="budget"
            placeholder="e.g. $1000, Budget-friendly, Luxury"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label className="block mb-2">Interests</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {interestOptions.map((option) => (
            <div key={option.id} className="flex items-center space-x-2">
              <Checkbox
                id={option.id}
                checked={selectedInterests.includes(option.id)}
                onCheckedChange={(checked) => handleInterestChange(option.id, checked === true)}
              />
              <label
                htmlFor={option.id}
                className="text-sm font-medium leading-none cursor-pointer"
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {showEmailField ? (
        <div className="space-y-4 pt-2">
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button 
            onClick={handleSendEmail} 
            className="w-full bg-travel-blue hover:bg-blue-700"
          >
            Send Itinerary to Email
          </Button>
        </div>
      ) : (
        <Button 
          onClick={handleGenerateItinerary} 
          className="w-full bg-travel-blue hover:bg-blue-700"
        >
          Generate Itinerary
        </Button>
      )}
    </div>
  );
};

export default ItineraryForm;
